import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  X, 
  Minimize2, 
  Maximize2, 
  Send, 
  ChevronLeft,
  Users,
  Check,
  CheckCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfileByUid } from '../firebase/firestore';
import { 
  subscribeToConversations, 
  subscribeToMessages, 
  sendMessage, 
  markConversationAsRead,
  createConversation,
  Conversation,
  Message
} from '../firebase/messaging';
import { subscribeToFriends, Friend } from '../firebase/friends';
import { subscribeToMultipleUserStatuses, UserStatus } from '../firebase/userStatus';

interface ChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  initialConversationId?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ isOpen, onClose, initialConversationId }) => {
  const { user } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [userStatuses, setUserStatuses] = useState<{ [userId: string]: UserStatus }>({});
  const [newMessage, setNewMessage] = useState('');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load user profile
  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      const profile = await getUserProfileByUid(user.uid);
      setUserProfile(profile);
    };
    loadProfile();
  }, [user]);

  // Subscribe to conversations
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToConversations(user.uid, (convs) => {
      setConversations(convs);
      // Calculate total unread
      const total = convs.reduce((acc, conv) => acc + (conv.unreadCount[user.uid] || 0), 0);
      setUnreadTotal(total);
      
      // If initial conversation is provided, select it
      if (initialConversationId && !selectedConversation) {
        const conv = convs.find(c => c.id === initialConversationId);
        if (conv) setSelectedConversation(conv);
      }
    });
    return unsubscribe;
  }, [user, initialConversationId, selectedConversation]);

  // Subscribe to friends
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToFriends(user.uid, setFriends);
    return unsubscribe;
  }, [user]);

  // Subscribe to user statuses
  useEffect(() => {
    if (friends.length === 0) return;
    const friendIds = friends.map(f => f.friendId);
    const unsubscribe = subscribeToMultipleUserStatuses(friendIds, setUserStatuses);
    return unsubscribe;
  }, [friends]);

  // Subscribe to messages
  useEffect(() => {
    if (!selectedConversation || !user) return;
    const unsubscribe = subscribeToMessages(selectedConversation.id, (msgs) => {
      setMessages(msgs);
      markConversationAsRead(selectedConversation.id, user.uid);
    });
    return unsubscribe;
  }, [selectedConversation, user]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user || !userProfile) return;
    try {
      await sendMessage(
        selectedConversation.id,
        user.uid,
        userProfile.displayName || 'User',
        userProfile.handle || '',
        userProfile.profileImageUrl,
        newMessage.trim()
      );
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const startNewConversation = async (friend: Friend) => {
    if (!user || !userProfile) return;
    
    // Check if conversation already exists
    const existingConv = conversations.find(conv => 
      conv.participants.includes(friend.friendId)
    );
    
    if (existingConv) {
      setSelectedConversation(existingConv);
      setShowNewChat(false);
      return;
    }

    // Create new conversation
    try {
      const participantData = {
        [user.uid]: {
          name: userProfile.displayName || 'User',
          handle: userProfile.handle || '',
          imageUrl: userProfile.profileImageUrl || ''
        },
        [friend.friendId]: {
          name: friend.friendName,
          handle: friend.friendHandle,
          imageUrl: friend.friendImageUrl || ''
        }
      };

      const conversationId = await createConversation(
        [user.uid, friend.friendId],
        participantData
      );

      // The conversation will appear via the subscription
      setShowNewChat(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const formatTime = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return timestamp.toLocaleDateString();
  };

  const getStatusColor = (status: UserStatus | undefined) => {
    if (!status) return 'bg-gray-400';
    switch (status.status) {
      case 'online': return 'bg-green-400';
      case 'away': return 'bg-yellow-400';
      case 'busy': return 'bg-red-400';
      default: return 'bg-gray-400';
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div 
      className={`fixed bottom-20 right-4 z-50 bg-dark-bg border border-dark-border rounded-lg shadow-2xl transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-80 sm:w-96 h-[500px]'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-accent-blue to-accent-green rounded-t-lg">
        <div className="flex items-center space-x-2">
          <MessageCircle className="w-5 h-5 text-white" />
          <span className="font-semibold text-white">Messages</span>
          {unreadTotal > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {unreadTotal}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            {isMinimized ? (
              <Maximize2 className="w-4 h-4 text-white" />
            ) : (
              <Minimize2 className="w-4 h-4 text-white" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="flex flex-col h-[calc(100%-52px)]">
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <div className="flex items-center px-4 py-2 border-b border-dark-border bg-dark-card">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-1 mr-2 hover:bg-dark-bg rounded transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-text-muted" />
                </button>
                {(() => {
                  const otherId = selectedConversation.participants.find(p => p !== user.uid);
                  const otherName = otherId ? selectedConversation.participantNames[otherId] : 'Unknown';
                  const otherImage = otherId ? selectedConversation.participantImages[otherId] : '';
                  const status = otherId ? userStatuses[otherId] : undefined;
                  
                  return (
                    <>
                      <div className="relative">
                        {otherImage ? (
                          <img src={otherImage} alt={otherName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-accent-blue rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-dark-card ${getStatusColor(status)}`} />
                      </div>
                      <span className="ml-2 font-medium text-text-primary text-sm truncate">{otherName}</span>
                    </>
                  );
                })()}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] ${msg.senderId === user.uid ? 'order-2' : ''}`}>
                      <div
                        className={`px-3 py-2 rounded-lg text-sm ${
                          msg.senderId === user.uid
                            ? 'bg-accent-blue text-white'
                            : 'bg-dark-card text-text-primary'
                        }`}
                      >
                        {msg.content}
                      </div>
                      <div className={`flex items-center mt-0.5 text-[10px] text-text-muted ${
                        msg.senderId === user.uid ? 'justify-end' : ''
                      }`}>
                        <span>{formatTime(msg.timestamp)}</span>
                        {msg.senderId === user.uid && (
                          msg.read ? (
                            <CheckCheck className="w-3 h-3 ml-1 text-accent-blue" />
                          ) : (
                            <Check className="w-3 h-3 ml-1" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-dark-border">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-dark-card border border-dark-border rounded-full px-4 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    className="p-2 bg-accent-blue rounded-full text-white hover:bg-accent-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          ) : showNewChat ? (
            <>
              {/* New Chat Header */}
              <div className="flex items-center px-4 py-2 border-b border-dark-border bg-dark-card">
                <button
                  onClick={() => setShowNewChat(false)}
                  className="p-1 mr-2 hover:bg-dark-bg rounded transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-text-muted" />
                </button>
                <span className="font-medium text-text-primary text-sm">New Message</span>
              </div>

              {/* Friends List */}
              <div className="flex-1 overflow-y-auto">
                {friends.length === 0 ? (
                  <div className="p-4 text-center text-text-muted text-sm">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No friends yet</p>
                    <p className="text-xs">Add friends to start chatting!</p>
                  </div>
                ) : (
                  friends.map((friend) => {
                    const status = userStatuses[friend.friendId];
                    return (
                      <button
                        key={friend.id}
                        onClick={() => startNewConversation(friend)}
                        className="w-full flex items-center p-3 hover:bg-dark-card transition-colors border-b border-dark-border"
                      >
                        <div className="relative">
                          {friend.friendImageUrl ? (
                            <img src={friend.friendImageUrl} alt={friend.friendName} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-accent-blue rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-dark-bg ${getStatusColor(status)}`} />
                        </div>
                        <div className="ml-3 text-left">
                          <p className="font-medium text-text-primary text-sm">{friend.friendName}</p>
                          <p className="text-xs text-text-muted">{friend.friendHandle}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {/* New Chat Button */}
                <button
                  onClick={() => setShowNewChat(true)}
                  className="w-full flex items-center p-3 hover:bg-dark-card transition-colors border-b border-dark-border"
                >
                  <div className="w-10 h-10 bg-accent-green rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="ml-3 font-medium text-text-primary text-sm">Start New Chat</span>
                </button>

                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-text-muted text-sm">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const otherId = conv.participants.find(p => p !== user.uid);
                    const otherName = otherId ? conv.participantNames[otherId] : 'Unknown';
                    const otherImage = otherId ? conv.participantImages[otherId] : '';
                    const status = otherId ? userStatuses[otherId] : undefined;
                    const unread = conv.unreadCount[user.uid] || 0;

                    return (
                      <button
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className="w-full flex items-center p-3 hover:bg-dark-card transition-colors border-b border-dark-border"
                      >
                        <div className="relative">
                          {otherImage ? (
                            <img src={otherImage} alt={otherName} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-accent-blue rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-dark-bg ${getStatusColor(status)}`} />
                        </div>
                        <div className="ml-3 flex-1 text-left min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-text-primary text-sm truncate">{otherName}</p>
                            <span className="text-[10px] text-text-muted ml-2 flex-shrink-0">
                              {conv.lastMessageTime ? formatTime(conv.lastMessageTime) : ''}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-xs text-text-muted truncate">{conv.lastMessage || 'No messages yet'}</p>
                            {unread > 0 && (
                              <span className="bg-accent-blue text-white text-[10px] px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0">
                                {unread}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWidget;


